import React, { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Badge, Grid } from 'antd';
import { BellFilled } from '@ant-design/icons';
import classNames from 'classnames';

import { Navigation, NavigationBody, NavigationFooter, NavigationHeader } from 'components/common';
import { Requests, Exit, Mail, Report, Settings, User, Calendar } from 'icons';
import { Language, Namespace, Path } from 'enums';
import { changeLanguage, getAllCompensations, getLanguage, getUser } from 'store';
import { environment } from 'environments';
import { useAppDispatch, useAppSelector, useLogout, usePermissions } from 'hooks';
import { fetchCurrentYearRequests } from 'services';

import { MenuGroupItem } from './MenuGroupItem';
import { MenuButton, MenuLink } from './MenuItem';
import styles from './Sidebar.module.scss';

export const Sidebar = () => {
  const { t } = useTranslation(Namespace.Navigation);
  const screens = Grid.useBreakpoint();
  const dispatch = useAppDispatch();
  const selectedLanguage = useAppSelector(getLanguage);
  const user = useAppSelector(getUser);
  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(false);
  const [subtitle, setSubtitle] = useState('');
  const permissions = usePermissions();
  const logout = useLogout();
  const compensations = useAppSelector(getAllCompensations).filter((item) => {
    return item.status === 'Pending';
  });

  useEffect(() => {
    if (user) {
      const subtitleByLanguage =
        selectedLanguage === Language.RU
          ? `${user.lastName} ${user.firstName}`
          : `${user.lastNameEn} ${user.firstNameEn}`;

      setSubtitle(subtitleByLanguage);
    }
  }, [selectedLanguage, user]);

  const changeLanguageHandler = useCallback(
    (language: Language) => {
      dispatch(changeLanguage(language));
    },
    [dispatch],
  );

  useEffect(() => {
    if (permissions.hasManageCompensationsPermission || permissions.hasAdministratorPermission) {
      dispatch(fetchCurrentYearRequests());
    }
  }, [dispatch, permissions]);

  const reportIssue = useCallback(() => window.showCollectorDialog(), []);
  const canManageApplications = permissions.hasManageCompensationsPermission || permissions.hasAdministratorPermission;
  const isCompact = isSidebarCollapsed || screens.md === false;
  const toggleSidebar = useCallback(() => setIsSidebarCollapsed((collapsed) => !collapsed), []);

  return (
    <Navigation
      className={classNames(styles.sidebar, {
        [styles.sidebarCollapsed]: isCompact,
      })}
    >
      {!isCompact && <NavigationHeader title="Benefits" subtitle={subtitle} />}
      <NavigationBody>
        <nav aria-label={t('navigation')}>
          <ul className={styles.navigation}>
            <li className={styles.group}>
              <MenuGroupItem
                text={t('personal')}
                visibleDefault
                collapsed={isCompact}
                groupItems={
                  <ul>
                    <li>
                      <MenuLink collapsed={isCompact} icon={<User />} text={t('personalArea')} path={Path.Home} />
                    </li>
                  </ul>
                }
              />
            </li>
            {canManageApplications && (
              <li className={styles.group}>
                <MenuGroupItem
                  text={t('management')}
                  visibleDefault
                  collapsed={isCompact}
                  groupItems={
                    <ul>
                      <li>
                        <MenuLink
                          collapsed={isCompact}
                          icon={<Requests />}
                          text={t('employeeRequests')}
                          path={Path.Requests}
                        >
                          <Badge count={compensations.length} overflowCount={999} color="#52c41a" />
                        </MenuLink>
                      </li>
                      <li>
                        <MenuLink collapsed={isCompact} icon={<Calendar />} text={t('archive')} path={Path.Archive} />
                      </li>
                      {permissions.hasAdministratorPermission && (
                        <>
                          <li>
                            <MenuLink collapsed={isCompact} icon={<Report />} text={t('reports')} path={Path.Reports} />
                          </li>
                          <li>
                            <MenuLink
                              collapsed={isCompact}
                              icon={<Settings />}
                              text={t('settings')}
                              path={Path.Settings}
                            />
                          </li>
                        </>
                      )}
                    </ul>
                  }
                />
              </li>
            )}
            <li className={styles.group}>
              <MenuGroupItem
                text={t('helpSupport')}
                visibleDefault
                collapsed={isCompact}
                groupItems={
                  <ul>
                    <li>
                      <MenuLink collapsed={isCompact} icon={<BellFilled />} text={t('news')} path={Path.News} />
                    </li>
                    <li>
                      <MenuButton
                        collapsed={isCompact}
                        icon={<Mail />}
                        text={t('reportAnIssue')}
                        onClick={reportIssue}
                      />
                    </li>
                    <li>
                      <MenuButton collapsed={isCompact} icon={<Exit />} text={t('logOut')} onClick={logout} />
                    </li>
                  </ul>
                }
              />
            </li>
          </ul>
        </nav>
      </NavigationBody>
      <NavigationFooter
        version={environment.appVersion}
        language={selectedLanguage}
        languages={[Language.EN, Language.RU]}
        onLanguageChange={changeLanguageHandler}
        collapsed={isCompact}
        onToggleCollapse={screens.md === false ? undefined : toggleSidebar}
        collapseLabel={t(isCompact ? 'expandSidebar' : 'collapseSidebar')}
      />
    </Navigation>
  );
};
